import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import {
    AcquisitionStrategy,
    ActualDataType,
    AggregationMode,
    ConnectionPointType,
    DataToApprove,
    Dictionary,
    DigitalSet,
    DigitalSetSimpleDictionary,
    Group,
    IApprovalButtonsConfiguration,
    IApprovalStrategy,
    IFrequency,
    IValueViewModel,
    IdentifierType,
    Interval,
    TagDefinition,
    TagManualInputValues,
    TagStringValueDto,
    ITreeItem,
    DashboardUsers
} from '../../../models';
import { Dispatcher, Store, StoreElement } from '../../../stateManagement';
import { IWidget, IBindableWidgetConfiguration } from '../../../widgets';
import {
    IGroupApprovalService,
    IRelativeGroupHelperService,
    GroupConfig,
    IDashboardLoadService,
    IStoreService,
    IDigitalSetHelperService,
    IGroupService,
    IIdentifierQueryService,
    IStringValuesHelperService
} from '../../../services';
import { momentFormat, toDictionary } from '../../../common/';
import { toTagManualInputValues } from '../../../widgets/manualInputs/manualInputs.helper';
import { ValidationStateStoreMap } from './validationStateStoreMap';
import { specialWidgetTypes } from './validation.constants';
import { DatesHelper } from '../../../common/datesHelper';
import { ValidationStateDispatchMap } from './validationStateDispatchMap';
import { buildAlarmList } from '../../../common/helpers/buildAlarmList';
import { ManualInputsWidgetConfiguration } from 'widgets/manualInputs/manualInputs.configuration';

export interface LoadObject {
    /**
     * Global approval configuration buttons.
     */
    config: IApprovalButtonsConfiguration;
    /**
     * Dictionary of frequency by widget.
     */
    frequencies: { [widgetGuid: string]: IFrequency };
    /**
     * Dictionary of promises to get group config per group.
     */
    groupConfigPromises: { [guid: string]: angular.IPromise<GroupConfig> };
    /**
     * Dictionary providing the group for each manual inputs or validation widget or spot manual inputs.
     */
    widgetGroups: { [widgetGuid: string]: Group };
}

export type ApprovalCallback = (level: number, status: number, dateToApprove?: Date) => angular.IPromise<void>;

export interface IValidationStateService {
    buildGenericApprovalConfig(
        config: GroupConfig,
        selectedDate: Date,
        interval: Interval,
        inWidget: boolean,
        callback: ApprovalCallback
    ): IApprovalButtonsConfiguration;
    connect: (scope: ValidationStateDispatchMap & ValidationStateStoreMap, selectedGroup: Group, appFrequency: IFrequency) => { unsubscribe: (() => void); firstLoad: angular.IPromise<void> };
    filterAndSortManualInputs(
        manualInputTagsPerGroup: Dictionary<TagManualInputValues[]>,
        group: Group,
        groups: Dictionary<Group>,
        configuration: ManualInputsWidgetConfiguration
    ): angular.IPromise<TagManualInputValues[]>;
    getEndDateToApprove(dataToApprove: DataToApprove, group: Group, appFrequency: IFrequency): Date;
    /**
     * Returns a new approval buttons configuration.
     * @param conf Approval buttons configuration
     * @param group Group
     * @param dates Dates to update
     */
    loadInitialObjects(
        widgets: any[],
        groupConfig: GroupConfig,
        referenceGroup: string,
        selectedGroup: Group,
        groupsDictionary: Dictionary<Group>,
        selectedDate: Date,
        interval: Interval,
        callback: ApprovalCallback
    ): LoadObject;
    /**
     * Loads and assign values to widgets using DashboardLoadService.
     * @param widgets 
     * @param beginDate 
     * @param endDate 
     * @param groupGuid 
     * @param force 
     */
    refreshAndAssign(
        widgets: IWidget[],
        beginDate: string,
        endDate: string,
        groupGuid: string,
        digitalSets: DigitalSetSimpleDictionary,
        stringValues: TagStringValueDto[],
        viewModels: Dictionary<IValueViewModel>,
        force?: boolean
    ): angular.IPromise<void>;
    updateApprovalButtons(
        config: IApprovalButtonsConfiguration,
        group: Group, 
        dates: Date[],
        beginDate:string,
        endDate:string
    ): angular.IPromise<IApprovalButtonsConfiguration>;

    postDeleteWidget(): angular.IPromise<[IValueViewModel[], Dispatcher<DashboardUsers>]>;
}

export class ValidationStateService implements IValidationStateService {
    static serviceId = 'validationStateService';

    static $inject = [
        '$q',
        'dashboardLoadService',
        'groupApprovalService',
        'relativeGroupHelperService',
        'storeService',
        'digitalSetHelperService',
        'groupService',
        'identifierQueryService',
        'stringValuesHelperService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _dashboardLoadService: IDashboardLoadService,
        private _groupApprovalService: IGroupApprovalService,
        private _relativeGroupHelperService: IRelativeGroupHelperService,
        private _storeService: IStoreService,
        private _digitalSetHelperService: IDigitalSetHelperService,
        private _groupService: IGroupService,
        private _identifierQueryService: IIdentifierQueryService,
        private _stringValuesHelperService: IStringValuesHelperService
    ) {
    }

    connect(scope: ValidationStateDispatchMap & ValidationStateStoreMap, selectedGroup: Group, appFrequency: IFrequency) {
        // We need a promise to know when loading is complete
        const defer = this._$q.defer<void>();
        scope.update = (type: string, referenceGroup: string, widgets: IWidget[]) => this._storeService.dispatchers().validationPageConfigurations.updateReferenceGroup(type, referenceGroup, widgets);
        const unsubscribe = this._storeService.register((s: Store) => {
            const storeResult = this.mapStateToThis(s, selectedGroup, appFrequency);
            (Object as any).assign(scope, storeResult);
            defer.resolve();
        }, [
            StoreElement.ApprovalStrategies,
            StoreElement.Alarms,
            StoreElement.ConnectionPoints,
            StoreElement.Dashboards,
            StoreElement.DigitalSets,
            StoreElement.Groups,
            StoreElement.Identifiers,
            StoreElement.Tags,
            StoreElement.ValidationPageConfigurations,
            StoreElement.ValueViewModels
        ]);
        return { unsubscribe, firstLoad: defer.promise };
    }

    private mapStateToThis(store: Store, group: Group, appFrequency: IFrequency): ValidationStateStoreMap {
        const {
            approvalStrategies,
            alarms,
            connectionPoints,
            dashboards,
            digitalSets,
            digitalSetDictionary,
            groups,
            tags,
            valueViewModels,
            validationPageConfigurations
        } = store;

        const spotManualTagsPerGroup: Dictionary<TagDefinition[]> = {};
        const manualInputTagsPerGroup: Dictionary<TagManualInputValues[]> = {};
        const spotManualTags: Dictionary<TagDefinition> = {};
        const manualTags: Dictionary<TagDefinition> = {};
        const digitalSetSelection = this.prepareDigitalSetSelection(digitalSets.dictionary);
        const manualCp = toDictionary(_.values(connectionPoints.dictionary)
            .filter(cp => cp.type === ConnectionPointType.Manual));

        if (Object.keys(manualCp).length) {
            _.values(tags.dictionary)
                .filter(t => manualCp[t.acquisitionPointGuid])
                .forEach(tag => {
                    if (tag.acquisitionStrategy === AcquisitionStrategy.Default)
                    {
                        manualTags[tag.guid] = tag;
                        this.prepareManualTagInGroup(
                            tag,
                            manualInputTagsPerGroup,
                            appFrequency,
                            valueViewModels.dictionary,
                            groups.dictionary,
                            digitalSetSelection
                        );
                    }
                    else if (tag.acquisitionStrategy === AcquisitionStrategy.Spot)
                    {
                        spotManualTags[tag.guid] = tag;
                        this.prepareSpotTagInGroup(
                            tag,
                            valueViewModels.dictionary,
                            spotManualTagsPerGroup,
                            groups.dictionary
                        );
                    }
                });
        }

        const referenceGroup = this._relativeGroupHelperService.getReferenceGroup(group, validationPageConfigurations.dictionary);
        const alarmList = buildAlarmList(alarms.dictionary, valueViewModels.dictionary);
        return {
            alarmList,
            approvalStrategies: approvalStrategies.dictionary,
            dashboards: dashboards.dictionary,
            digitalSetDictionary: digitalSets.dictionary,
            digitalSets: digitalSetDictionary.dictionary,
            groups: _.values(groups.dictionary),
            groupsDictionary: groups.dictionary,
            manualInputTagsPerGroup,
            referenceGroup,
            spotManualTagsPerGroup,
            viewModels: valueViewModels.dictionary,
            validationPageConfigurations: validationPageConfigurations.dictionary
        };
    }

    /**
     * Prepare the TagManualInputValues and set fill the manualInputTagsPerGroup
     */
    private prepareManualTagInGroup(
        tag: TagDefinition,
        manualInputTagsPerGroup: Dictionary<TagManualInputValues[]>,
        appFrequency: IFrequency,
        viewModels: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        digitalSets: Dictionary<DigitalSet>
    ): void {
        const tagVM = viewModels[tag.guid];

        const manualInputValues = toTagManualInputValues(tagVM, appFrequency);
        manualInputValues.dataType = tag.actualDataType;
        if (tag.actualDataType === ActualDataType.Digital) {
            // digitalSets are copies prepared with empty choice
            manualInputValues.digitalSet = digitalSets[tag.digitalSetGuid];
        }

        tagVM.groupList.forEach(group => {
            const tagsForGroup = manualInputTagsPerGroup[group.guid] ?? [];

            // find matching identifier(s) and assign view models
            const toAdd = groups[group.guid]
                .identifierCollection
                .filter(i => i.tagDefinitionGuid === tag.guid)
                .sort((a, b) => a.sortingOrder - b.sortingOrder)
                .map(i => {
                    const copy = angular.copy(manualInputValues);
                    copy.identifierVm = viewModels[i.guid];
                    copy.viewModel = viewModels[tag.guid];
                    return copy;
                });

            tagsForGroup.push(...toAdd);
            manualInputTagsPerGroup[group.guid] = tagsForGroup;
        });
    }

    /**
     * Prepare the spotPerGroup dictionary
     * @param tag
     * @param dictionary
     */
    private prepareSpotTagInGroup(
        tag: TagDefinition,
        viewModels: Dictionary<IValueViewModel>,
        spotsPerGroup: Dictionary<TagDefinition[]>,
        groups: Dictionary<Group>
    ): void {
        const x = viewModels[tag.guid]
            .groupList
            .forEach(g => {
                const spots = spotsPerGroup[g.guid] ?? [];
                const shouldAdd = groups[g.guid]
                    .identifierCollection
                    .some(i => i.type !== IdentifierType.CalculatedValue && i.tagDefinitionGuid === tag.guid);
                if (shouldAdd) {
                    spots.push(tag);
                    spotsPerGroup[g.guid] = spots;
                }
            });
    }

    private prepareDigitalSetSelection(digitalSets: Dictionary<DigitalSet>): Dictionary<DigitalSet> {
        const result: Dictionary<DigitalSet> = {};

        _.values(digitalSets)
            .map(s => angular.copy(s))
            .forEach(copy => {
                copy.stateCollection.unshift({ code: null, name: '' });
                result[copy.guid] = copy;
            });

        return result;
    }

    filterAndSortManualInputs(
        manualInputTagsPerGroup: Dictionary<TagManualInputValues[]>,
        group: Group,
        groups: Dictionary<Group>,
        configuration: ManualInputsWidgetConfiguration
    ): angular.IPromise<TagManualInputValues[]> {
        let inputList = manualInputTagsPerGroup[group.guid]?.map(d => d) ?? [];
        inputList = this.filterForChildren(configuration, group, groups, inputList, manualInputTagsPerGroup);
        let filterByQueryPromise: angular.IPromise<TagManualInputValues[]>;
        if (configuration.identifierQueries?.length) {
            filterByQueryPromise = this._$q.all(configuration.identifierQueries.map(q => this._identifierQueryService.execute(q)))
                .then(data => {
                    const concat: IValueViewModel[] = _.uniq([].concat(...data));
                    const vmGuids = _.uniq(concat.map(c => c.guid));
                    inputList = inputList.filter(il => vmGuids.indexOf(il.identifierVm?.guid) >= 0);
                    return inputList;
                });

        } else {
            filterByQueryPromise = this._$q.when(inputList);
        }
        return filterByQueryPromise
            .then(il => il.sort((a, b) => this.sortManualInputValues(a, b, groups)));
    }

    private sortManualInputValues(a: TagManualInputValues, b: TagManualInputValues, groups: Dictionary<Group>) {
        let result = 0;
        if (a.identifierVm && a.identifierVm.groupList?.length &&
            b.identifierVm && b.identifierVm.groupList?.length) {
            result = a.identifierVm.groupUniqueName === b.identifierVm.groupUniqueName
                ? 0
                : a.identifierVm.groupUniqueName < b.identifierVm.groupUniqueName ? -1 : 1;
            if (result === 0) {
                const group = groups[a.identifierVm.groupList[0].guid];
                if (group) {
                    const identifiers = toDictionary(group.identifierCollection);
                    const identifierA = identifiers[a.identifierVm.guid];
                    const identifierB = identifiers[b.identifierVm.guid];
                    if (identifierA && identifierB) {
                        result = identifierA.sortingOrder < identifierB.sortingOrder ? -1 : 1;
                    }
                }
            }
        }
        return result;
    }

    private filterForChildren(
        configuration: ManualInputsWidgetConfiguration,
        group: Group,
        groups: Dictionary<Group>,
        inputList: TagManualInputValues[],
        manualInputTagsPerGroup: Dictionary<TagManualInputValues[]>,
    ): TagManualInputValues[] {
        if (configuration.includeChildren) {
            const groupsTree = this._groupService.convertToTree(_.values(groups));
            const treeItem = _.find(groupsTree, t => t.guid === group.guid);
            if (treeItem) {
                const children = treeItem.children;
                if (children) {
                    inputList = this.recurse(children, inputList, manualInputTagsPerGroup);
                }
            }
        }
        return inputList;
    }

    private recurse(
        children: ITreeItem<Group>[],
        inputList: TagManualInputValues[],
        manualInputTagsPerGroup: Dictionary<TagManualInputValues[]>
    ): TagManualInputValues[] {
        try {
            children.forEach(child => {
                const mipt = manualInputTagsPerGroup[child.guid];
                if (mipt && mipt.length) {
                    inputList = inputList.concat(mipt);
                }
                if (child.children) {
                    inputList = this.recurse(child.children, inputList, manualInputTagsPerGroup);
                }
            });
        }
        catch (e) {
            console.error(e);
        }
        return inputList;
    }

    /**
     * Returns a new approval buttons configuration.
     * @param conf Approval buttons configuration
     * @param group Group
     * @param dates Dates to update
     */
    updateApprovalButtons(
        config: IApprovalButtonsConfiguration,
        group: Group,
        dates: Date[],
        beginDate:string,
        endDate:string
    ): angular.IPromise<IApprovalButtonsConfiguration> {
        let promise: angular.IPromise<IApprovalButtonsConfiguration>;
        const maxLevel = config?.levelCount ?? 0;
        // If maxLevel === 0, config not yet loaded => pointless to make mapping
        if (maxLevel > 0) {
            const moments = dates.map(d => moment(d));
            const datesAsString = _.uniq(moments.map(m => m.toISOString()));
            const dateKeys: string[] = moments.map(m => m.format(momentFormat));
            promise = this._groupApprovalService
                .getDetails(group.guid, beginDate, endDate, { hasAlarm: false, hasQuality: false, isTreeMode: false, readTuples: [] } )
                .then(data => {
                    const copy = angular.copy(config);
                    copy.approvalsByDate = {};
                    // TODO remove usage of approvals and only use approvalsByDate
                    if (dateKeys && dateKeys.length === 1) {
                        // TODO find why:
                        // Format from API is different from format in UI
                        datesAsString[0] = datesAsString[0].replace(':00.000Z', ':00Z');
                        copy.approvals = data.find(
                          (d) => d.guid === group.guid
                        )?.statuses.find(s => s.timeStamp === datesAsString[0])?.approvals;
                    } else {
                        // DateTime and Date object doesn't have the same format as string by default, so we need to use Date format for dictionary key.
                        const statuses = data.find((d) => d.guid === group.guid)?.statuses;
                        const datesWithApproval = statuses.map(s => s.timeStamp);
                        const dateKeyList = [];
                        datesWithApproval.forEach(dateWithApproval => {
                            const dateKey = moment(dateWithApproval).format(momentFormat);
                            copy.approvalsByDate[dateKey] = statuses.find(s => s.timeStamp === dateWithApproval)?.approvals;
                            dateKeyList.push(dateKey);
                        });
                    }
                    return copy;
                });
        } else {
            promise = this._$q.when(config);
        }
        return promise;
    }

    loadInitialObjects(
        widgets: any[],
        groupConfig: GroupConfig,
        referenceGroup: string,
        selectedGroup: Group,
        groupsDictionary: Dictionary<Group>,
        selectedDate: Date,
        interval: Interval,
        callback: (level, status, dateToApprove?) => angular.IPromise<void>
    ) {
        const { widgetGroups, frequencies } = this.buildDictionaries(widgets, referenceGroup, selectedGroup, groupsDictionary);
        const approvableWidgets = widgets.filter(w => (w.configuration as any).enableApproval);
        const groupGuids = approvableWidgets
            .map(w => widgetGroups[w.guid].guid)
            .filter(g => g !== selectedGroup.guid);
        // Hide global if there is multiple group refered into page
        const uniqueGuids = _.uniq(groupGuids);
        const hasMultipleApprovalGroup = uniqueGuids.length > 1
            || (uniqueGuids.length === 1 && uniqueGuids[0] !== selectedGroup.guid);

        const groupConfigPromises: { [key: string]: angular.IPromise<GroupConfig> } = {};
        // Add current group if not present as we always want it
        groupConfigPromises[selectedGroup.guid] = this._$q.resolve<GroupConfig>(groupConfig);
        // Add all other widgets
        uniqueGuids.forEach(g => groupConfigPromises[g] = this._groupApprovalService.getGroupConfig(g));

        const config: IApprovalButtonsConfiguration = this.buildGenericApprovalConfig(
            groupConfig,
            selectedDate,
            interval,
            false,
            callback
        );
        config.hide = hasMultipleApprovalGroup;
        return {
            widgetGroups,
            frequencies,
            groupConfigPromises,
            config
        };
    }

    buildGenericApprovalConfig(
        config: GroupConfig,
        selectedDate: Date,
        interval: Interval,
        inWidget: boolean,
        callback: ApprovalCallback
    ): IApprovalButtonsConfiguration {
        const groupConfig: IApprovalButtonsConfiguration = {
            allowFuture: config.isFutureAllowed,
            approvals: [],
            approve: (level, status, dateToApprove?) => callback(level, status, dateToApprove),
            currentUserLevels: config.userLevel,
            hideIcon: inWidget,
            interval: interval,
            inWidget: inWidget,
            levelCount: config.maxLevel,
            timestamp: selectedDate
        };
        return groupConfig;
    }

    getEndDateToApprove(dataToApprove: DataToApprove, group: Group, appFrequency: IFrequency): Date {
        let groupInterval = group.frequency.interval;
        let groupTimeUnit = group.frequency.intervalTimeUnit;
        // Fallback: if group has no frequency, use app frequency
        if (groupInterval === 0) {
            groupInterval = appFrequency.interval;
            groupTimeUnit = appFrequency.intervalTimeUnit;
        }
        const intervalUnitString = DatesHelper.timeUnitToStartOf(groupTimeUnit);
        dataToApprove.endDateToApprove = moment(dataToApprove.dateToApprove).add(groupInterval, intervalUnitString).toDate();
        return dataToApprove.endDateToApprove;
    }

    refreshAndAssign(widgets: IWidget[], beginDate: string, endDate: string, groupGuid: string, digitalSets: DigitalSetSimpleDictionary, stringValues: TagStringValueDto[], viewModels: Dictionary<IValueViewModel>, force: boolean = false): angular.IPromise<void> {
        return this._dashboardLoadService
            .refresh(widgets, beginDate, endDate, groupGuid, undefined, undefined, force)
            .then(data => {
                // TODO should be done inside assignValues
                data = this._digitalSetHelperService.mapDigitalSetsValues(data, digitalSets, viewModels, AggregationMode.None);
                data = this._stringValuesHelperService.mapStringValues(data, stringValues, viewModels, AggregationMode.None);
                data = this._dashboardLoadService.setIdentifiers(data, viewModels);
                this._dashboardLoadService.assignValues(data, widgets);
            });
    }

    postDeleteWidget(): angular.IPromise<[IValueViewModel[], Dispatcher<DashboardUsers>]> {
        return this._$q.all([
            this._storeService.dispatchers().valueViewModels.getAll(true),
            this._storeService.dispatchers().dashboardUsers.getAll(true)
        ]);
    }

    private buildDictionaries(
        widgets: { type: string; configuration: IBindableWidgetConfiguration; guid: string }[],
        referenceGroup: string,
        selectedGroup: Group,
        groupsDictionary: { [guid: string]: Group }
    ) {
        const widgetGroups: { [widgetGuid: string]: Group } = {};
        const frequencies: { [widgetGuid: string]: IFrequency } = {};
        widgets.filter(w => this.filterSpecialWidget(w))
            .forEach(w => {
                const group = this.getGroup(w.configuration, referenceGroup, selectedGroup, groupsDictionary);
                widgetGroups[w.guid] = group;
                frequencies[w.guid] = group.frequency;
            });
        return { widgetGroups, frequencies };
    }

    private getGroup(
        configuration: IBindableWidgetConfiguration,
        referenceGroup: string,
        selectedGroup: Group,
        groupsDictionary: Dictionary<Group>
    ): Group {
        if (referenceGroup) {
            configuration = (Object as any).assign({}, configuration, { referenceGroup: referenceGroup });
        }
        return this._relativeGroupHelperService.getGroup(configuration, selectedGroup, groupsDictionary);
    }

    /**
     * Filter widget with possible interactions (manual input, validation)
     */
    private filterSpecialWidget(w: { type: string }): boolean {
        return specialWidgetTypes.indexOf(w.type) >= 0;
    }
}